import "~/css/Modal.css";
import "~/css/StandbyScreen.css";
import React, { useEffect, useState, useCallback, CSSProperties, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import {
  characterIndexAtom,
  currentVRMAtom,
  difyWarmupCompletedAtom,
  voicevoxWarmupCompletedAtom,
  aivisspeechWarmupCompletedAtom,
  faceModelLoadedAtom,
  isFaceDetectionAtom,
  isAllLoadedAtom,
  isListeningAtom,
  isProgressModalReadyAtom,
  isStandbyModeAtom,
  isThinkingAtom,
  languageAtom,
  model3dLoadedAtom,
  motion3dLoadedAtom,
  T2SEngineAtom,
  volumeAtom,
  vrmListAtom,
  vrmPathAtom,
  currentSpeakerAtom,
  isTranscribingAtom,
} from "~/Atom";
import { Canvas } from "@react-three/fiber";
import { isCallOutAtom, isGreeterAtom, styleModeAtom } from "~/AtomWithDynamoDB";
import * as THREE from "three";
import { OrbitControls } from "@react-three/drei";
import Model from "~/Model";
import { ModalProps } from "shared/Schemas";
import RubyText from "~/components/utils/RubyText";
import { getVRMGlobalName, getVRMName } from "~/vrm_utils/VRMUtils";
import * as icon from "~/Icons";
import * as db from "~/components/db/CharacterDBUtils";
import { PlaySound, playVoice, PlayVoiceParams } from "~/SoundUtils";
import { Character } from "shared/Schemas";
import { currentLocale } from "~/Commons";
import { useCustomVariables } from "~/components/settings/HamburgerMenu/hooks/useCustomVariables";
import { useProgressModal } from "~/components/hooks/useProgressModal";
import ProgressModal from "~/components/modal/ProgressModal";
import { warmupVoicevox, warmupAivisSpeech } from "~/utils/VoiceWarmup";

const canvasStyle: CSSProperties = {
  borderRadius: "10px",
  display: "block",
  position: "absolute",
  height: "75% !important",
  zIndex: "-1",
};
// 3D関連の設定
const cameraConfig = {
  fov: 20, // 視野角（Field of View）
  near: 0.1, // 描画する最小距離
  far: 100, // 描画する最大距離
  position: [0, -0.04, 0.5] as [number, number, number], // カメラ位置
};

// ライティング関連の設定
const lighting = {
  directionalLight: {
    position: {
      x: 5, // 平行光源のX座標
      y: 10, // 平行光源のY座標
      z: 10, // 平行光源のZ座標
    },
    color: 0xffffff, // 光源の色（白）
    intensity: 3.0, // 光源の強さ
  },
  ambientLight: {
    color: 0xffffff, // 環境光の色（白）
    intensity: 0.5, // 環境光の強さ
  },
};

// GL設定関連の設定
const glConfig = {
  antialias: true,
  alpha: true,
  outputColorSpace: THREE.SRGBColorSpace, // 出力色空間
  toneMapping: THREE.NeutralToneMapping, // トーンマッピング方式
  toneMappingExposure: 1.0, // トーンマッピングの露出
};

// キャラクター切り替え方向の定数
const characterDirection = {
  next: 1, // 次のキャラクターへ進む
  previous: -1, // 前のキャラクターへ戻る
} as const;

type DirectionType = typeof characterDirection.next | typeof characterDirection.previous;

/**
 * 時刻に応じた挨拶を取得する
 * @param hour 時刻（0-23）
 * @param t 翻訳関数
 * @returns 挨拶文字列
 */
export const getTimeBasedGreeting = (
  hour: number,
  t: (key: string) => string,
  styleMode: string
): string => {
  if (hour >= 5 && hour < 12) {
    return t(`timeBasedGreetings.${styleMode}.morning`);
  } else if (hour >= 12 && hour < 18) {
    return t(`timeBasedGreetings.${styleMode}.afternoon`);
  } else {
    return t(`timeBasedGreetings.${styleMode}.evening`);
  }
};

/**
 * スタンバイ画面コンポーネント
 * キャラクター選択とモデル表示のためのモーダル
 */
const StandbyScreen: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { greeting } = useCustomVariables();
  const [, setIsStandbymode] = useAtom(isStandbyModeAtom);
  const [, setIsModelViewed] = useState(false);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [name, setName] = useState<string>("");
  const [globalName, setGlobalName] = useState<string>("");
  const [language] = useAtom(languageAtom);
  const [isListening] = useAtom(isListeningAtom);
  const [isThinking] = useAtom(isThinkingAtom);
  const [volume] = useAtom(volumeAtom);
  const [vrmList] = useAtom(vrmListAtom);
  const [, setVRMPath] = useAtom(vrmPathAtom);
  const [, setCharacterIndex] = useAtom(characterIndexAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [isAllLoaded] = useAtom(isAllLoadedAtom);
  const [vrmPath] = useAtom(vrmPathAtom);
  const [isCallOut] = useAtom(isCallOutAtom);
  const [isGreeter] = useAtom(isGreeterAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [, setIsTranscribing] = useAtom(isTranscribingAtom);
  const [styleMode] = useAtom(styleModeAtom);

  // ボタン無効化条件を変数として抽出
  const isControlDisabled = (isListening && isThinking) || !isAllLoaded;

  // プログレスモーダルの管理
  const [, setIsProgressModalReady] = useAtom(isProgressModalReadyAtom);
  const [isFaceDetection] = useAtom(isFaceDetectionAtom);
  const [isFaceModelLoaded] = useAtom(faceModelLoadedAtom);
  const [isModel3dLoaded] = useAtom(model3dLoadedAtom);
  const [isMotion3dLoaded] = useAtom(motion3dLoadedAtom);
  const [isDifyWarmupCompleted] = useAtom(difyWarmupCompletedAtom);
  const [isVoicevoxWarmupCompleted, setIsVoicevoxWarmupCompleted] = useAtom(
    voicevoxWarmupCompletedAtom
  );
  const [isAivisspeechWarmupCompleted, setIsAivisspeechWarmupCompleted] = useAtom(
    aivisspeechWarmupCompletedAtom
  );
  const {
    isProgressModalOpen,
    progressValue,
    errorMessage,
    openProgressModal,
    closeProgressModal,
    completedProcessKeys,
    completeProcess,
    setErrorMessage,
  } = useProgressModal();
  const warmupVoicevoxExecutedRef = useRef(false);
  const warmupAivisSpeechExecutedRef = useRef(false);

  /**
   * スタンバイスクリーン初期化完了時に準備完了フラグをセット
   */
  useEffect(() => {
    setIsProgressModalReady(true);
    return () => {
      setIsProgressModalReady(false);
    };
  }, [setIsProgressModalReady]);

  /**
   * 初期化完了Atomを監視して自動的にcompleteProcessを実行
   */
  useEffect(() => {
    if (!isProgressModalOpen) return;

    // 各リソースの読み込み状態を定義
    // 顔認識がOFFの場合、face_modelは即完了扱いにする
    const loadedStatus = [
      { isLoaded: !isFaceDetection || isFaceModelLoaded, key: "face_model" },
      { isLoaded: isModel3dLoaded, key: "3d_model" },
      { isLoaded: isMotion3dLoaded, key: "3d_motion" },
      { isLoaded: isDifyWarmupCompleted, key: "dify_warmup" },
      { isLoaded: isVoicevoxWarmupCompleted, key: "voicevox_warmup" },
      { isLoaded: isAivisspeechWarmupCompleted, key: "aivisspeech_warmup" },
    ];

    // 各リソースの状態を確認して、完了していればcompleteProcessを呼び出す
    loadedStatus.forEach(({ isLoaded, key }) => {
      if (isLoaded && !completedProcessKeys.has(key)) {
        completeProcess(key);
      }
    });
  }, [
    isProgressModalOpen,
    isFaceDetection,
    isFaceModelLoaded,
    isModel3dLoaded,
    isMotion3dLoaded,
    isDifyWarmupCompleted,
    isVoicevoxWarmupCompleted,
    isAivisspeechWarmupCompleted,
    completedProcessKeys,
    completeProcess,
  ]);

  useEffect(() => {
    // 6つのプロセスで管理:
    // - 3Dモデル読み込み完了(3d_model)
    // - 3Dモーション読み込み完了(3d_motion)
    // - 顔検出モデル読み込み完了(face_model)
    // - DifyAPIウォームアップ(dify_warmup)
    // - VOICEVOXウォームアップ(voicevox_warmup)
    // - AivisSpeechウォームアップ(aivisspeech_warmup)
    openProgressModal([
      "3d_model",
      "3d_motion",
      "face_model",
      "dify_warmup",
      "voicevox_warmup",
      "aivisspeech_warmup",
    ]);

    // VOICEVOXウォームアップを実行
    if (!warmupVoicevoxExecutedRef.current) {
      warmupVoicevoxExecutedRef.current = true;
      warmupVoicevox()
        .then(() => {
          setIsVoicevoxWarmupCompleted(true);
        })
        .catch((error) => {
          console.error("VOICEVOX warmup error:", error);
          // エラーメッセージをプログレスモーダルに表示
          setErrorMessage(t("systemStartupError"));
          warmupVoicevoxExecutedRef.current = false;
        });
    }

    // AivisSpeechウォームアップを実行
    if (!warmupAivisSpeechExecutedRef.current) {
      warmupAivisSpeechExecutedRef.current = true;
      warmupAivisSpeech()
        .then(() => {
          setIsAivisspeechWarmupCompleted(true);
        })
        .catch((error) => {
          console.error("AivisSpeech warmup error:", error);
          // エラーメッセージをプログレスモーダルに表示
          setErrorMessage(t("systemStartupError"));
          warmupAivisSpeechExecutedRef.current = false;
        });
    }
  }, []);

  /**
   * モーダルの表示状態に応じてスタンバイモードを設定
   */
  useEffect(() => {
    if (!isOpen) {
      setIsStandbymode(false);
    } else {
      setIsStandbymode(true);
    }
  }, [isOpen, setIsStandbymode]);

  /**
   * 現在のVRMからキャラクター名を取得して状態を更新
   */
  useEffect(() => {
    const fetchCharacterName = async () => {
      if (currentVRM !== null) {
        const cachedName = getVRMName(currentVRM);
        const cachedGlobalName = await getVRMGlobalName(currentVRM);
        if (cachedName) {
          setName(cachedName);
        }
        if (cachedGlobalName) {
          setGlobalName(cachedGlobalName);
        }
      }
    };

    fetchCharacterName();
  }, [currentVRM, language, vrmPath, isAllLoaded]);

  /**
   * モデル表示完了時の処理
   */
  const handleModelViewed = useCallback(() => {
    setIsModelViewed(true);
  }, []);

  /**
   * 独り言処理
   * isGreeterが有効でモーダルが開いている場合に1分毎に挨拶フレーズを再生
   * @return クリーンアップ関数
   */
  useEffect(() => {
    // 独り言が無効、またはモーダル非表示時は何もしない
    if (!isGreeter || !isOpen) {
      return undefined;
    }

    // 独り言フレーズリストを取得
    const greeterList = t(`GreeterList.${styleMode}`, { returnObjects: true }) as string[];
    if (!Array.isArray(greeterList) || greeterList.length === 0) {
      return undefined;
    }

    // 1分毎にフレーズを再生する処理
    const playGreeterPhrase = () => {
      // 文字起こしを無効に設定
      setIsTranscribing(false);
      // ランダムにフレーズを選択
      const randomPhrase = greeterList[Math.floor(Math.random() * greeterList.length)];

      // 時刻に応じた挨拶を取得
      const currentHour = new Date().getHours();
      const timeBasedGreeting = getTimeBasedGreeting(currentHour, t, styleMode);

      // 挨拶+フレーズを作成
      const fullText = `${timeBasedGreeting} ${randomPhrase}`;

      const params: PlayVoiceParams = {
        inputText: fullText,
        T2SEngine,
        currentSpeaker,
        speed: 1.0,
        pitch: 0.0,
        intonation: 1.0,
        volume: volume * 1.25, // やや大きめに再生
        talkingMode: 9, // チャット履歴に残さない
        setIsTranscribing,
      };

      playVoice(params);
    };

    // 1分毎に再生するインターバルを設定
    const intervalSeconds = 60;
    const intervalId = setInterval(playGreeterPhrase, intervalSeconds * 1000);

    // コンポーネントアンマウント時にインターバルをクリア
    const cleanup = (): void => {
      clearInterval(intervalId);
    };

    return cleanup;
  }, [isGreeter, isOpen, currentSpeaker, T2SEngine, volume, styleMode]);

  /**
   * キャラクター変更の共通ロジック
   * 指定された方向（次へ/前へ）に応じてキャラクターを切り替える
   *
   * @param direction - 切り替え方向（NEXT: 1 または PREVIOUS: -1）
   */
  const changeCharacter = useCallback(
    (direction: DirectionType) => {
      // 今のキャラクター名を取得
      const currentCharacter = getVRMName(currentVRM);

      db.selectAvailableCharacters(vrmList).then((characters: Character[]) => {
        if (characters.length === 0) return;

        // 現在のインデックスを取得
        let currentIndex = characters.findIndex(
          (character: Character) => character.name === currentCharacter
        );
        if (currentIndex < 0) currentIndex = 0;

        // 次または前のインデックスを計算（配列の境界を越えたら循環）
        const newIndex = (currentIndex + direction + characters.length) % characters.length;

        // VRMを取得
        const vrmData = vrmList.filter(
          (vrm) => vrm.name === characters[newIndex].global_name + ".vrm"
        )[0];
        if (!vrmData) return;

        setCharacterIndex(newIndex);
        setVRMPath(vrmData.url);

        // TTS設定
        if (language === currentLocale) {
          const ttsEngine = characters[newIndex].tts_engine;
          setT2SEngine(ttsEngine);
        } else {
          setT2SEngine("OpenAI");
        }
      });

      PlaySound("push", volume);
    },
    [currentVRM, language, vrmList, setCharacterIndex, setVRMPath, setT2SEngine, volume]
  );

  /**
   * 次のキャラクターへ切り替え
   */
  const setNextCharacter = useCallback(() => {
    changeCharacter(characterDirection.next);
  }, [changeCharacter]);

  /**
   * 前のキャラクターへ切り替え
   */
  const setPrevCharacter = useCallback(() => {
    changeCharacter(characterDirection.previous);
  }, [changeCharacter]);

  return (
    <>
      <ProgressModal
        isOpen={isProgressModalOpen}
        progress={progressValue}
        title={t("systemStarting")}
        message={t("systemStartingMessage")}
        errorMessage={errorMessage}
        onClose={closeProgressModal}
      />
      <div className={`modal-overlay ${isOpen ? "visible" : "hide"}`}>
        <div className={`standby-modal ${isOpen ? "visible" : "hide"}`}>
          <div className="standby-modal-header font-default-bold-24">
            <h2>{greeting()}</h2>
          </div>

          <div className={"standby-modal-content"}>
            <div className={"standby-modal-serifu"}>
              <p>
                {t(`standbyScreen.${styleMode}.letUsHelpYou`)}
                <br />
                {isCallOut
                  ? t(`standbyScreen.${styleMode}.callout`)
                  : t(`standbyScreen.${styleMode}.touch`)}
              </p>
            </div>

            <div className={"standby-canvas"}>
              <div className={"standby-name"}>
                <RubyText text={name} />
                <div className={"standby-name-global"} style={{ scale: "100%" }}>
                  <RubyText text={globalName} />
                </div>
              </div>

              <div className="character-button">
                <div className="change-btn">
                  <button
                    className="btn btn-circle"
                    onMouseUp={setPrevCharacter}
                    disabled={isControlDisabled}
                  >
                    <icon.icons_arrow_left />
                  </button>
                  <button
                    className="btn btn-circle"
                    onMouseUp={setNextCharacter}
                    disabled={isControlDisabled}
                  >
                    <icon.icons_arrow_right />
                  </button>
                </div>
              </div>

              <Canvas
                className={`standby-canvas ${isOpen ? "visible" : "hide"}`}
                camera={cameraConfig}
                flat
                style={canvasStyle}
                gl={glConfig}
              >
                {/* eslint-disable react/no-unknown-property */}
                <directionalLight
                  position-x={lighting.directionalLight.position.x}
                  position-y={lighting.directionalLight.position.y}
                  position-z={lighting.directionalLight.position.z}
                  color={lighting.directionalLight.color}
                  intensity={lighting.directionalLight.intensity}
                />
                <OrbitControls enableZoom={false} enableRotate={false} enablePan={false} />
                <ambientLight
                  color={lighting.ambientLight.color}
                  intensity={lighting.ambientLight.intensity}
                />
                <Model onViewed={handleModelViewed} />
              </Canvas>
            </div>
          </div>

          <button className="standby-modal-button" onMouseUp={onClose} disabled={!isAllLoaded}>
            <p className={"jp fadeBlink"}>{t(`standbyScreen.${styleMode}.pleaseTouchTheButton`)}</p>
            <p className={"en fadeBlink"}>
              {t(`standbyScreen.${styleMode}.pleaseTouchTheButtonEN`)}
            </p>
          </button>
        </div>
      </div>
    </>
  );
};

export default StandbyScreen;
